import {
  Box,
  Button,
  Form,
  FormField,
  Icon,
  Input,
  Select,
  SelectProps,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3';
import { v4 as uuid } from 'uuid';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePapaParse } from 'react-papaparse';
import FileSaver from 'file-saver';
import _ from 'lodash';
import { Redirect } from 'react-router-dom';
import {
  getCategoryOptions,
  getSubcategoryOptions,
  sortOptions,
} from '../../../../utils/ContainerManagement/containerDefinitionsHelper';
import '../style.css';
import { validateInput } from '../../../../utils';
import { ContainerDefinition } from '../../../../services/container-management/types';

const CreateBin = (props: any): ReactElement => {
  const { t } = useTranslation();
  const { jsonToCSV } = usePapaParse();
  const [siteId, setSiteId] = useState('');
  const [isSubmit, setIsSubmit] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<SelectProps.Option[]>([]);
  const [categorySelectedOption, setCategorySelectedOption] = useState<SelectProps.Option | null>(
    null
  );
  const [subcategoryOptions, setSubcategoryOptions] = useState<SelectProps.Option[]>([]);
  const [subcategorySelectedOption, setSubcategorySelectedOption] =
    useState<SelectProps.Option | null>(null);
  const [roomOptions, setRoomOptions] = useState([]);
  const [roomSelectedOption, setRoomSelectedOption] = useState([]);
  const [numberOfBins, setNumberOfBins] = useState(1);
  const [redirect, setRedirect] = useState('');
  const {
    failedRequests,
    flex,
    virtual,
    prefill,
    prefillValues,
    validations,
    rooms,
    categoriesMap,
    containerDefinitionMap,
  } = props;
  const [preFill] = useState(prefill);
  const [virtualType, setVirtualType] = useState(virtual);
  const [validationsRequired, setValidationsRequired] = useState(validations);
  const [flexInputType, setFlexInputType] = useState(flex);
  const [alphabetIdentifier, setAlphabetIdentifier] = useState('');
  const [containerNamePrefix, setContainerNamePrefix] = useState('');
  const [disableAddButton, setDisableAddButton] = useState(true);
  const initialValues = {
    containerDefinitionId: '',
    category: '',
    subcategory: '',
    room: '',
    roomLocationId: '',
  };
  const [formValues, setFormValues] = useState(initialValues);
  const [formErrors, setFormErrors] = useState({});
  const [binDetails, setBinDetails] = useState([
    {
      id: uuid(),
      siteRoom: '',
      section: '',
      alphabet: '',
      aisle: '',
      level: '',
      cell: '',
      errors: {
        siteRoom: '',
        section: '',
        alphabet: '',
        aisle: '',
        level: '',
        cell: '',
      },
    },
  ]);

  const [binDetailsFlex, setBinDetailsFlex] = useState([
    {
      id: uuid(),
      siteRoom: '',
      cell: '',
      errors: {
        siteRoom: '',
        cell: '',
      },
    },
  ]);

  // Sets Categories data on page load.
  const setCategoryData = () => {
    setCategoryOptions(getCategoryOptions(categoriesMap));
  };
  // Sets Rooms data
  const setRoomData = () => {
    setRoomOptions(rooms);
  };

  // Handles fields reset on dependent field change.
  const handleFieldReset = () => {
    setNumberOfBins(0);
    setBinDetails([]);
    setBinDetailsFlex([]);
  };

  // Manage side effect on page load
  useEffect(() => {
    const loadData = () => {
      setCategoryData();
      setRoomData();
      if (localStorage.getItem('siteId')) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        setSiteId(JSON.parse(localStorage.getItem('siteId')!).value);
      }
    };
    loadData();
    handleFieldReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesMap]);

  // Handles category change.
  const handleCategoryChange = useCallback(
    (obj: any) => {
      setVirtualType(false);
      setFlexInputType(false);
      setValidationsRequired(false);
      setBinDetailsFlex([]);
      setSubcategorySelectedOption(null);
      formValues.category = obj.value;
      _.set(formErrors, ['category'], '');
      setCategorySelectedOption(obj);
      setSubcategoryOptions(getSubcategoryOptions(categoriesMap, obj));
    },
    [formValues.category, formErrors, categoriesMap]
  );

  useEffect(() => {
    const loadPrefillData = () => {
      setIsSubmit(false);
      const categoryPrefillOption = categoryOptions.find((obj: any) => {
        return obj.label === failedRequests.formValues.category;
      });

      if (categoryPrefillOption) {
        handleCategoryChange(categoryPrefillOption);
      }
      const subcategoryPrefillOption = subcategoryOptions.find((obj: any) => {
        return obj.label === failedRequests.formValues.subcategory;
      });

      const roomPrefillOption = roomOptions.find((obj: any) => {
        return obj.label === failedRequests.formValues.room;
      });

      if (categoryPrefillOption) {
        setCategorySelectedOption(categoryPrefillOption);
      }

      if (subcategoryPrefillOption) {
        setSubcategorySelectedOption(subcategoryPrefillOption);
      }

      if (roomPrefillOption) {
        setRoomSelectedOption(roomPrefillOption);
      }
      setVirtualType(virtual);
      setFlexInputType(flex);
      _.set(formValues, 'category', failedRequests.formValues.category);
      _.set(formValues, 'subcategory', failedRequests.formValues.subcategory);
      _.set(formValues, 'room', failedRequests.formValues.room);
      _.set(formValues, 'roomLocationId', failedRequests.formValues.roomLocationId);
      _.set(formValues, 'containerDefinitionId', failedRequests.formValues.containerDefinitionId);
      setBinDetails(failedRequests.binDetails);

      if (virtualType && flexInputType) {
        setNumberOfBins(failedRequests.binDetailsFlex.length);
        setBinDetailsFlex(failedRequests.binDetailsFlex);
      } else {
        setNumberOfBins(failedRequests.binDetails.length);
      }
      setDisableAddButton(false);
      setValidationsRequired(validations);
    };
    if (failedRequests) {
      loadPrefillData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categoryOptions,
    failedRequests,
    flex,
    flexInputType,
    formValues,

    preFill,
    prefillValues,
    roomOptions,
    virtual,
    virtualType,
  ]);

  // Handles subcategory change
  const handleSubcategoryChange = (obj: any) => {
    handleFieldReset();
    formValues.subcategory = obj.label;
    formValues.containerDefinitionId = obj.value;
    _.set(formErrors, ['subcategory'], '');
    setSubcategorySelectedOption(obj);

    const containerDefinition: ContainerDefinition = containerDefinitionMap?.get(obj.value);

    if (containerDefinition?.metadataConfiguration.virtualBinType?.default) {
      setVirtualType(true);
      setFlexInputType(true);
    }
    setValidationsRequired(
      !!containerDefinition?.metadataConfiguration.validationsRequired?.default
    );
    if (containerDefinition?.metadataConfiguration.containerNamePrefix) {
      setContainerNamePrefix(containerDefinition.metadataConfiguration.containerNamePrefix.default);
      setFlexInputType(true);
    } else if (containerDefinition?.metadataConfiguration.alphabetIdentifier) {
      setAlphabetIdentifier(containerDefinition.metadataConfiguration.alphabetIdentifier.default);
      setFlexInputType(false);
    }
  };

  // Handles room change
  const handleRoomChange = (obj: any) => {
    _.set(formErrors, ['room'], '');
    formValues.room = obj.value;
    formValues.roomLocationId = obj.locationId;
    handleFieldReset();
    setRoomSelectedOption(obj);
    setDisableAddButton(false);
  };

  // Removes the set of input fields for room site , section , aisle , level and cell.
  const handleRemoveFields = () => {
    if (flexInputType) {
      const values = [...binDetailsFlex];
      values.splice(values.length - 1, 1);
      setBinDetailsFlex(values);
      setNumberOfBins(values.length);
    } else {
      const values = [...binDetails];
      values.splice(values.length - 1, 1);
      setBinDetails(values);
      setNumberOfBins(values.length);
    }
  };

  // Autofills the set of input fields for room site , section , aisle , level and cell.
  const handleAutoFillFields = () => {
    const firstBin = binDetails[0];
    let incrementValue = Number(firstBin.cell.replace(/^\D+/g, ''));
    const cellValue = firstBin.cell.replace(/\d+$/, '');
    const newBins = binDetails.map((obj, index) => {
      if (index < 1) {
        return obj;
      }
      incrementValue += 1;
      return {
        ...obj,
        section: firstBin.section,
        aisle: firstBin.aisle,
        level: firstBin.level,
        alphabet: firstBin.alphabet,
        cell:
          cellValue +
          incrementValue.toLocaleString('en-US', {
            minimumIntegerDigits: 1,
            useGrouping: false,
          }),
      };
    });
    setBinDetails(newBins);
  };
  // Autofills the set of input fields for cell.
  const handleAutoFillFieldsVirtual = () => {
    const firstBin = binDetailsFlex[0];
    let incrementValue = Number(firstBin.cell.replace(/^\D+/g, ''));
    const cellValue = firstBin.cell.replace(/\d+$/, '');
    const newBins = binDetailsFlex.map((obj, index) => {
      if (index < 1) {
        return obj;
      }
      incrementValue += 1;
      return {
        ...obj,
        siteRoom: firstBin.siteRoom,
        cell:
          cellValue +
          incrementValue.toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false,
          }),
      };
    });
    setBinDetailsFlex(newBins);
  };

  // validates all the dropdowns
  const validateDropdowns = () => {
    const errors = {};

    if (!formValues.category) _.set(errors, ['category'], 'Category is required!');
    if (!formValues.subcategory) _.set(errors, ['subcategory'], 'Subcategory is required!');
    if (!formValues.room) _.set(errors, ['room'], 'Room is required!');
    if (binDetails.length === 0 && !flexInputType) _.set(errors, ['bins'], 'No bins added');
    if (binDetailsFlex.length === 0 && flexInputType) _.set(errors, ['binsFlex'], 'No bins added');

    setFormErrors(errors);
    return _.isEmpty(errors);
  };

  const validate = () => {
    if (binDetails.length === 0) {
      return true;
    }
    const errorFields = binDetails.some(
      (item) =>
        validateInput(item.section, false, validationsRequired) !== '' ||
        validateInput(item.aisle, false, validationsRequired) !== '' ||
        validateInput(item.alphabet, false, validationsRequired) !== '' ||
        validateInput(item.level, false, validationsRequired) !== '' ||
        validateInput(item.cell, true, validationsRequired) !== ''
    );

    if (errorFields) {
      // eslint-disable-next-line array-callback-return
      binDetails.map((item, index) => {
        const allPrev = [...binDetails];
        allPrev[index].errors.section = validateInput(
          binDetails[index].section,
          false,
          validationsRequired
        );
        allPrev[index].errors.aisle = validateInput(
          binDetails[index].aisle,
          false,
          validationsRequired
        );
        allPrev[index].errors.level = validateInput(
          binDetails[index].level,
          false,
          validationsRequired
        );
        allPrev[index].errors.alphabet = validateInput(
          binDetails[index].alphabet,
          false,
          validationsRequired
        );
        allPrev[index].errors.cell = validateInput(
          binDetails[index].cell,
          true,
          validationsRequired
        );
        setBinDetails(allPrev);
      });
    }

    return !errorFields;
  };

  const validateFlex = () => {
    if (binDetailsFlex.length === 0) {
      return true;
    }
    const errorFields = binDetailsFlex.some(
      (item) => validateInput(item.cell, true, validationsRequired) !== ''
    );
    if (errorFields) {
      // eslint-disable-next-line array-callback-return
      binDetailsFlex.map((item, index) => {
        const allPrev = [...binDetailsFlex];
        allPrev[index].errors.cell = validateInput(
          binDetailsFlex[index].cell,
          true,
          validationsRequired
        );
        setBinDetailsFlex(allPrev);
      });
    }
    return !errorFields;
  };

  // Adds a set of input fields for bin name , section , aisle , level and cell.
  // Or bin name and number based on the type.
  const handleAddFields = () => {
    if (flexInputType) {
      _.set(formErrors, ['binsFlex'], '');
      setBinDetailsFlex([
        ...binDetailsFlex,
        {
          id: uuid(),
          siteRoom: `${siteId}.${JSON.parse(JSON.stringify(roomSelectedOption)).value}`,
          cell: '',
          errors: {
            siteRoom: '',
            cell: '',
          },
        },
      ]);
      const values = [...binDetailsFlex];
      setNumberOfBins(values.length + 1);
    } else {
      _.set(formErrors, ['bins'], '');
      setBinDetails([
        ...binDetails,
        {
          id: uuid(),
          siteRoom: `${siteId}.${JSON.parse(JSON.stringify(roomSelectedOption)).value}`,
          section: '',
          alphabet: alphabetIdentifier,
          aisle: '',
          level: '',
          cell: '',
          errors: {
            siteRoom: '',
            section: '',
            alphabet: '',
            aisle: '',
            level: '',
            cell: '',
          },
        },
      ]);
      const values = [...binDetails];
      setNumberOfBins(values.length + 1);
    }
  };

  // Handles number of bins change
  const handleNumberOfBinsCountChange = (count: any) => {
    const bins: any[] = [];
    if (Number(count) <= 50) {
      if (!flexInputType) {
        for (let i = 0; i < Number(count); i += 1) {
          bins.push({
            id: uuid(),
            siteRoom: `${siteId}.${JSON.parse(JSON.stringify(roomSelectedOption)).value}`,
            section: '',
            alphabet: alphabetIdentifier,
            aisle: '',
            level: '',
            cell: '',
            errors: {
              siteRoom: '',
              section: '',
              alphabet: '',
              aisle: '',
              level: '',
              cell: '',
            },
          });
        }
        setBinDetails(bins);
      } else {
        for (let i = 0; i < Number(count); i += 1) {
          bins.push({
            id: uuid(),
            siteRoom: `${siteId}.${
              JSON.parse(JSON.stringify(roomSelectedOption)).value
            }.${containerNamePrefix}`,
            cell: '',
            errors: {
              siteRoom: '',
              cell: '',
            },
          });
        }
        setBinDetailsFlex(bins);
      }
      setNumberOfBins(bins.length);
    } else {
      setBinDetails([]);
      setBinDetailsFlex([]);
      setNumberOfBins(Number(count));
    }
  };

  // Handles input change and adds it to the binDetailsFlex
  const handleInputChangeFlex = (id: string, obj: any, name: string) => {
    const newInputFields = binDetailsFlex.map((i) => {
      if (id === i.id) {
        if (name === 'cell') {
          // eslint-disable-next-line no-param-reassign
          i.cell = obj.toUpperCase();
          // eslint-disable-next-line no-param-reassign
          i.errors.cell = validateInput(i.cell, true, validationsRequired);
        }
      }
      return i;
    });
    setBinDetailsFlex(newInputFields);
  };

  // Handles input change and adds it to the binDetails
  const handleInputChange = (id: string, obj: any, name: string) => {
    const newInputFields = binDetails.map((i) => {
      if (id === i.id) {
        switch (name) {
          case 'section':
            // eslint-disable-next-line no-param-reassign
            i.section = obj.toUpperCase();
            // eslint-disable-next-line no-param-reassign
            i.errors.section = validateInput(i.section, false, validationsRequired);
            break;
          case 'alphabetIdentifier':
            // eslint-disable-next-line no-param-reassign
            i.alphabet = obj.toUpperCase();
            // eslint-disable-next-line no-param-reassign
            i.errors.alphabet = validateInput(i.alphabet, false, validationsRequired);
            break;
          case 'aisle':
            // eslint-disable-next-line no-param-reassign
            i.aisle = obj.toUpperCase();
            // eslint-disable-next-line no-param-reassign
            i.errors.aisle = validateInput(i.aisle, false, validationsRequired);
            break;
          case 'level':
            // eslint-disable-next-line no-param-reassign
            i.level = obj.toUpperCase();
            // eslint-disable-next-line no-param-reassign
            i.errors.level = validateInput(i.level, false, validationsRequired);
            break;
          case 'cell':
            // eslint-disable-next-line no-param-reassign
            i.cell = obj.toUpperCase();
            // eslint-disable-next-line no-param-reassign
            i.errors.cell = validateInput(i.cell, true, validationsRequired);
            break;
          default:
            break;
        }
      }
      return i;
    });
    setBinDetails(newInputFields);
  };

  // Handles submit button
  const handleSubmit = (e: any) => {
    e.preventDefault();
    props.setHasSubmitted(true);
    if (!virtualType) {
      if (validate() && validateDropdowns()) {
        setIsSubmit(true);
        props.setFormValuesBin({
          formValues,
          binDetails,
          binDetailsFlex,
          isSubmit: true,
          flex: flexInputType,
          virtual: virtualType,
          validations: validationsRequired,
        });
      }
    } else if (validateFlex() && validateDropdowns()) {
      setIsSubmit(true);
      props.setFormValuesBin({
        formValues,
        binDetails,
        binDetailsFlex,
        isSubmit: true,
        flex: flexInputType,
        virtual: virtualType,
        validations: validationsRequired,
      });
    }
  };

  // Handles reset button
  const handleResetButton = () => {
    setCategorySelectedOption(null);
    setSubcategorySelectedOption(null);
    setRoomSelectedOption(roomOptions[-1]);
    setNumberOfBins(0);
    setBinDetails([]);
    setBinDetailsFlex([]);
    setFormErrors({});
    setFormValues(initialValues);
    setDisableAddButton(true);
  };

  // Creates dropdown with parameters
  const createDropDown = (option: any, onchange: any, options: any, name: any) => {
    return (
      <Select
        selectedOption={option}
        onChange={({ detail }) => onchange(detail.selectedOption)}
        options={options}
        placeholder="Choose an option"
        selectedAriaLabel="Selected"
        empty="No options"
        className={name}
        name={name}
        disabled={isSubmit}
      />
    );
  };
  const createInputField = (
    placeholder: any,
    value: any,
    onchange: any,
    id: any,
    name: string,
    disabled?: boolean
  ) => {
    if (disabled) {
      return (
        <Input
          placeholder={placeholder}
          id={id}
          className={name}
          value={value}
          name={name}
          disabled
        />
      );
    }
    return (
      <Input
        placeholder={placeholder}
        id={id}
        className={name}
        value={value}
        name={name}
        disabled={isSubmit}
        onChange={({ detail }) => onchange(id, detail.value, name)}
      />
    );
  };
  const downloadCsv = () => {
    const blob = new Blob([`${jsonToCSV(props.csvData)}`], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, `${props.csvFileName}.csv`);
  };

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button data-test-id="cancelButton" onClick={() => setRedirect('/container')}>
              Cancel
            </Button>
            <Button
              className="submitButton"
              disabled={isSubmit}
              variant="primary"
              onClick={handleSubmit}>
              {t('create-bin.title')}
            </Button>
            <Button disabled={!isSubmit} variant="primary" onClick={downloadCsv}>
              Download
            </Button>
          </SpaceBetween>
        }>
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xxl">
            <div className="resetButtonStyle">
              <Button
                id="reset"
                formAction="none"
                className="resetButton"
                iconAlign="right"
                disabled={isSubmit}
                onClick={() => handleResetButton()}>
                Reset
              </Button>
            </div>
          </SpaceBetween>
        </Box>
        <SpaceBetween direction="vertical" size="l">
          <SpaceBetween direction="vertical" size="l">
            <FormField label={t('create-bin.category-label')}>
              <div className="dropDownStyle">
                {createDropDown(
                  categorySelectedOption,
                  handleCategoryChange,
                  sortOptions(categoryOptions),
                  'category'
                )}
                {JSON.parse(JSON.stringify(formErrors)).category && (
                  <small className="categoryError" style={{ color: 'red' }}>
                    {JSON.parse(JSON.stringify(formErrors)).category}
                  </small>
                )}
              </div>
            </FormField>
            <FormField label={t('create-bin.subcategory-label')}>
              <div className="dropDownStyle">
                {createDropDown(
                  subcategorySelectedOption,
                  handleSubcategoryChange,
                  sortOptions(subcategoryOptions),
                  'subcategory'
                )}
                {JSON.parse(JSON.stringify(formErrors)).subcategory && (
                  <small className="subCategoryError" style={{ color: 'red' }}>
                    {JSON.parse(JSON.stringify(formErrors)).subcategory}
                  </small>
                )}
              </div>
            </FormField>
            <FormField label={t('create-bin.room-label')}>
              <div className="dropDownStyle">
                {createDropDown(
                  roomSelectedOption,
                  handleRoomChange,
                  sortOptions(roomOptions),
                  'room'
                )}
                {JSON.parse(JSON.stringify(formErrors)).room && (
                  <small className="roomError" style={{ color: 'red' }}>
                    {JSON.parse(JSON.stringify(formErrors)).room}
                  </small>
                )}
              </div>
            </FormField>
            <FormField label={t('create-bin.no-of-bins')}>
              <SpaceBetween direction="horizontal" size="xs">
                <div
                  style={{
                    width: '50px',
                    overflow: 'hidden',
                  }}>
                  <Input
                    className="numberOfBins"
                    value={numberOfBins.toString()}
                    name="numberOfBins"
                    disabled={isSubmit}
                    onChange={({ detail }) => handleNumberOfBinsCountChange(detail.value)}
                  />
                </div>
                <Button
                  className="addButton"
                  formAction="none"
                  iconAlign="right"
                  disabled={disableAddButton || numberOfBins >= 50 || isSubmit}
                  onClick={() => handleAddFields()}>
                  <Icon name="add-plus" />
                </Button>
                <Button
                  className="removeButton"
                  formAction="none"
                  iconAlign="right"
                  disabled={numberOfBins === 0 || isSubmit}
                  onClick={() => handleRemoveFields()}>
                  -
                </Button>
                {binDetails.length > 1 && binDetails[0].cell !== '' && (
                  <Button
                    className="autofillButton"
                    formAction="none"
                    iconAlign="right"
                    disabled={disableAddButton || numberOfBins === 0 || isSubmit}
                    onClick={() => handleAutoFillFields()}>
                    Autofill
                  </Button>
                )}
                {binDetailsFlex.length > 1 && binDetailsFlex[0].cell !== '' && (
                  <Button
                    className="autofillButton"
                    formAction="none"
                    iconAlign="right"
                    disabled={disableAddButton || numberOfBins === 0 || isSubmit}
                    onClick={() => handleAutoFillFieldsVirtual()}>
                    Autofill
                  </Button>
                )}
              </SpaceBetween>
              {JSON.parse(JSON.stringify(formErrors)).bins && !flexInputType && (
                <small className="binsEmptyError" style={{ color: 'red' }}>
                  {JSON.parse(JSON.stringify(formErrors)).bins}
                </small>
              )}
              {JSON.parse(JSON.stringify(formErrors)).binsFlex && flexInputType && (
                <small className="binsEmptyErrorFlex" style={{ color: 'red' }}>
                  {JSON.parse(JSON.stringify(formErrors)).binsFlex}
                </small>
              )}
              {numberOfBins > 50 && (
                <small className="binLimitError" style={{ color: 'red' }}>
                  Cannot add more than 50 Bins
                </small>
              )}
            </FormField>
            {!virtualType && (
              <FormField label={binDetails.length > 0 ? t('create-bin.bin-name') : ''}>
                <SpaceBetween direction="horizontal" size="xs">
                  {!flexInputType &&
                    binDetails.map((inputField) => (
                      <div
                        id="inputDiv"
                        key={inputField.id}
                        style={{
                          width: '700px',
                          overflow: 'hidden',
                        }}>
                        <SpaceBetween direction="horizontal" size="xs">
                          {/* TODO: add validations for the fields  */}
                          <div className="siteRoomStyle">
                            {createInputField(
                              'Bin Name',
                              inputField.siteRoom,
                              handleInputChange,
                              inputField.id,
                              'siteRoom',
                              true
                            )}
                          </div>
                          <div className="inputStyle">
                            {createInputField(
                              'Alphabet',
                              inputField.alphabet,
                              handleInputChange,
                              inputField.id,
                              'alphabetIdentifier'
                            )}
                            {inputField.errors.alphabet && (
                              <small
                                style={{
                                  color: 'red',
                                }}
                                className="alphabetError">
                                {inputField.errors.alphabet}
                              </small>
                            )}
                          </div>
                          <div className="inputStyle">
                            {createInputField(
                              'Section',
                              inputField.section,
                              handleInputChange,
                              inputField.id,
                              'section'
                            )}
                            {inputField.errors.section && (
                              <small
                                className="sectionError"
                                style={{
                                  color: 'red',
                                }}>
                                {inputField.errors.section}
                              </small>
                            )}
                          </div>
                          <div className="inputStyle">
                            {createInputField(
                              'Aisle',
                              inputField.aisle,
                              handleInputChange,
                              inputField.id,
                              'aisle'
                            )}
                            {inputField.errors.aisle && (
                              <small
                                style={{
                                  color: 'red',
                                }}
                                className="aisleError">
                                {inputField.errors.aisle}
                              </small>
                            )}
                          </div>
                          <div className="inputStyle">
                            {createInputField(
                              'Level',
                              inputField.level,
                              handleInputChange,
                              inputField.id,
                              'level'
                            )}
                            {inputField.errors.level && (
                              <small
                                style={{
                                  color: 'red',
                                }}
                                className="levelError">
                                {inputField.errors.level}
                              </small>
                            )}
                          </div>
                          <div className="cellStyle">
                            {createInputField(
                              'Cell*',
                              inputField.cell,
                              handleInputChange,
                              inputField.id,
                              'cell'
                            )}
                            {inputField.errors.cell && (
                              <small
                                style={{
                                  color: 'red',
                                }}
                                className="cellError">
                                {inputField.errors.cell}
                              </small>
                            )}
                          </div>
                        </SpaceBetween>
                      </div>
                    ))}
                </SpaceBetween>
              </FormField>
            )}
            {virtualType && (
              <FormField label={binDetailsFlex.length > 0 ? t('create-bin.bin-name') : ''}>
                <SpaceBetween direction="horizontal" size="xs">
                  {binDetailsFlex.map((inputField) => (
                    <div
                      id="inputDiv"
                      key={inputField.id}
                      style={{
                        width: '700px',
                        overflow: 'hidden',
                      }}>
                      <SpaceBetween direction="horizontal" size="xs">
                        {/* TODO: add validations for the fields  */}
                        <div className="dropDownStyle">
                          {createInputField(
                            'Bin Name',
                            inputField.siteRoom,
                            handleInputChange,
                            inputField.id,
                            'siteRoom',
                            true
                          )}
                        </div>
                        <div className="dropDownStyle">
                          {createInputField(
                            'Container Name',
                            inputField.cell,
                            handleInputChangeFlex,
                            inputField.id,
                            'cell'
                          )}
                          {inputField.errors.cell && (
                            <small
                              className="cellError"
                              style={{
                                color: 'red',
                              }}>
                              {inputField.errors.cell}
                            </small>
                          )}
                        </div>
                      </SpaceBetween>
                    </div>
                  ))}
                </SpaceBetween>
              </FormField>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Form>
    </form>
  );
};

export default CreateBin;
